import type { ForecastChange, InstanceDetail } from "@keydials/api-client";
import { changesIn, fmtCompareDate, forecastReason } from "./compareModel";

/**
 * KEY-890: Look ahead's "What's coming" — what changes by itself before the chosen day, for whatever the card is
 * showing, in the order it happens: "Fri 14 Nov · Dentist (Health) · Booked → Due soon · its due date".
 */
export function ComingPanel({ detail, changes, day, scope, scopeName, statusName }: {
  detail: InstanceDetail;
  changes: ForecastChange[] | null;
  day: string;
  scope?: { sectionKey: string; segmentKey?: string | null };
  scopeName: string;
  statusName: (key: string) => string;
}) {
  const date = fmtCompareDate(day);
  const rows = changes ? changesIn(changes, scope) : null;
  const nodeName = (c: ForecastChange) => {
    const section = detail.sections.find((s) => s.key === c.sectionKey);
    const segment = section?.segments.find((g) => g.key === c.segmentKey);
    const item = c.itemKey ? segment?.items.find((i) => i.key === c.itemKey) : undefined;
    const where = segment?.name ?? c.segmentKey;
    return item ? `${item.name} (${where})` : where;
  };
  return (
    <section className="kd-card" aria-label={`What's coming before ${date}`} style={{ padding: "var(--pad-card)", display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "var(--text)" }}>
          Coming up to {date} · in {scopeName}
        </h3>
        {rows && <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{rows.length === 0 ? "Nothing changes by itself" : `${rows.length} change${rows.length === 1 ? "" : "s"}`}</span>}
      </div>
      <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-faint)" }}>If nothing is done — what dates, changes back and resets will do on their own.</p>
      {!rows ? (
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-faint)" }}>Looking ahead…</p>
      ) : rows.length > 0 && (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {rows.slice(0, 80).map((c, i) => (
            <li key={`${c.on}-${c.sectionKey}-${c.segmentKey}-${c.itemKey ?? ""}-${c.dimensionKey ?? ""}-${i}`} className="kd-changes-row">
              <span className="kd-changes-date">{fmtCompareDate(c.on)}</span>
              <span style={{ minWidth: 0 }}>
                <strong style={{ fontWeight: 700 }}>{nodeName(c)} · </strong>
                {statusName(c.fromStatusKey)} → {statusName(c.toStatusKey)}
              </span>
              <span className="kd-changes-who">{forecastReason(c.reason)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
